import React, { useState } from "react";

const RHCSA = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const syllabusData = {
    courseOverview: {
      title: "Course Overview",
      content: (
        <>
          <p className="font-bold text-sm">Objective:</p> Learn the core skills
          required to become a Red Hat Certified System Administrator (RHCSA) by
          mastering Linux system administration on Red Hat Enterprise Linux
          (RHEL).
          <p className="font-bold text-sm">Duration:</p> 45 days, 1 hour per
          day.
          <p className="font-bold text-sm">Prerequisites:</p> Basic
          understanding of Linux or Unix systems; familiarity with command-line
          interfaces is helpful.
          <p className="font-bold text-sm">Tools Needed:</p> A computer with
          virtualization software (e.g., VirtualBox, VMware), Red Hat Enterprise
          Linux 8/9 (or CentOS/Fedora for practice), and internet access.
        </>
      ),
    },
    dailySchedule: {
      title: "Daily Schedule",
      days: [
        {
          day: "Day 1: Introduction to Red Hat Enterprise Linux",
          objective: "Understand RHEL and set up the lab environment.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is RHEL? Overview of Red Hat certifications.</li>
                <li>RHEL features and architecture.</li>
                <li>Installing RHEL in a virtual machine.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Install RHEL and log in to the system.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 2: Basic Command-Line Skills",
          objective: "Learn essential Linux commands and navigation.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Navigating the file system (pwd, cd, ls).</li>
                <li>File handling commands (cp, mv, rm, touch).</li>
                <li>Viewing files (cat, less, head, tail).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create, copy, and delete files and directories.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 3: File and Directory Permissions",
          objective: "Understand file permissions and ownership.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding permissions (read, write, execute).</li>
                <li>Changing permissions with chmod and chown.</li>
                <li>Special permissions (SUID, SGID, sticky bit).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Modify permissions for a user and group.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 4: Managing Users and Groups",
          objective: "Learn to manage user accounts and groups.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating and managing users (useradd, passwd).</li>
                <li>Managing groups (groupadd, usermod).</li>
                <li>Understanding /etc/passwd and /etc/group files.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a user and add them to a group.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 5: Managing Processes",
          objective: "Understand process management in Linux.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Viewing processes (ps, top).</li>
                <li>Managing processes (kill, pkill, nice).</li>
                <li>Background and foreground processes (jobs, fg, bg).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Monitor and terminate a process.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 6: Working with Text Editors",
          objective: "Learn to edit files using vi/vim and nano.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to vi/vim and nano.</li>
                <li>Basic vi/vim commands (edit, save, quit).</li>
                <li>Search and replace in vi/vim.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Edit a configuration file using vi/vim.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 7: File System Navigation and Structure",
          objective: "Understand the Linux file system hierarchy.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Linux file system structure (/etc, /var, /home).</li>
                <li>Links: hard links and symbolic links (ln).</li>
                <li>Finding files (find, locate).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a symbolic link and locate a file.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 8: Managing Software with RPM and YUM/DNF",
          objective: "Learn to install and manage software packages.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding RPM (Red Hat Package Manager).</li>
                <li>Using YUM/DNF for package management.</li>
                <li>Installing, updating, and removing packages.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Install a package using DNF.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 9: Managing Repositories",
          objective: "Configure and manage software repositories.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding repositories in RHEL.</li>
                <li>Adding and enabling repositories.</li>
                <li>Managing repository priorities.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add a new repository and install a package from it.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 10: System Boot Process",
          objective: "Understand the RHEL boot process.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of the boot process (BIOS, GRUB2).</li>
                <li>Managing GRUB2 configuration.</li>
                <li>Understanding systemd targets.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Modify GRUB2 settings to change the default boot target.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 11: Managing Services with systemd",
          objective: "Learn to manage services using systemd.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding systemd and systemctl.</li>
                <li>Starting, stopping, and enabling services.</li>
                <li>Analyzing system logs with journalctl.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Enable and start a service (e.g., httpd).</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 12: Managing Logs",
          objective: "Learn to manage and analyze system logs.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding journalctl and systemd logs.</li>
                <li>Filtering and searching logs.</li>
                <li>Configuring log rotation with logrotate.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Search for errors in system logs using journalctl.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 13: Scheduling Tasks",
          objective: "Learn to schedule tasks using cron and at.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using cron for recurring tasks.</li>
                <li>Using at for one-time tasks.</li>
                <li>Managing cron jobs for users.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Schedule a backup script to run daily using cron.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 14: Managing Storage – Partitions",
          objective: "Learn to manage disk partitions.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding disk partitions (fdisk, parted).</li>
                <li>Creating and deleting partitions.</li>
                <li>Formatting partitions (ext4, xfs).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create and format a new partition.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 15: Managing Storage – File Systems",
          objective: "Learn to manage file systems and mount points.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Mounting and unmounting file systems.</li>
                <li>Configuring /etc/fstab for persistent mounts.</li>
                <li>Checking file system integrity (fsck).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Mount a new partition and add it to /etc/fstab.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 16: Logical Volume Manager (LVM) – Basics",
          objective: "Understand the basics of LVM.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to LVM (Physical Volumes, Volume Groups).</li>
                <li>Creating Logical Volumes.</li>
                <li>Formatting and mounting LVM volumes.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a Logical Volume and mount it.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 17: LVM – Advanced",
          objective: "Learn advanced LVM operations.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Resizing Logical Volumes and Volume Groups.</li>
                <li>Creating LVM snapshots.</li>
                <li>Removing LVM components.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Resize a Logical Volume and create a snapshot.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 18: Managing Swap Space",
          objective: "Learn to configure and manage swap space.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding swap space.</li>
                <li>Creating and enabling swap partitions/files.</li>
                <li>Adjusting swap priority (swappiness).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create and activate a swap file.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 19: Network Configuration – Basics",
          objective: "Learn to configure networking in RHEL.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding network interfaces (nmcli, nmtui).</li>
                <li>Configuring static IP addresses.</li>
                <li>Testing network connectivity (ping, traceroute).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure a static IP address using nmcli.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 20: Network Configuration – Advanced",
          objective: "Explore advanced network configurations.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configuring DNS settings (/etc/resolv.conf).</li>
                <li>Managing hostnames and /etc/hosts.</li>
                <li>Network troubleshooting (netstat, ss).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure DNS and test name resolution.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 21: Firewall Management with firewalld",
          objective: "Learn to configure firewalld for security.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to firewalld.</li>
                <li>Managing zones and services (firewall-cmd).</li>
                <li>Opening and closing ports.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Allow HTTP traffic through the firewall.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 22: SELinux – Basics",
          objective: "Understand SELinux and its basic configuration.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is SELinux? Modes (enforcing, permissive).</li>
                <li>Viewing SELinux status (getenforce, sestatus).</li>
                <li>Understanding SELinux contexts (ls -Z).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Check SELinux status and change its mode.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 23: SELinux – Managing Contexts",
          objective: "Learn to manage SELinux contexts and policies.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Changing SELinux file contexts (chcon, restorecon).</li>
                <li>Managing SELinux booleans (getsebool, setsebool).</li>
                <li>Troubleshooting SELinux issues (audit2allow).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Fix an SELinux context issue for a web server file.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 24: SSH Configuration",
          objective: "Learn to configure and secure SSH access.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding SSH and OpenSSH (sshd).</li>
                <li>Configuring SSH (password-based, key-based).</li>
                <li>Securing SSH (disable root login, change port).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure key-based SSH authentication.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 25: Remote File Access with NFS",
          objective: "Learn to configure NFS for file sharing.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to NFS (Network File System).</li>
                <li>Setting up an NFS server and client.</li>
                <li>Mounting NFS shares.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Share a directory using NFS and mount it on a client.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 26: Managing Time Services",
          objective: "Learn to configure time synchronization.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding time synchronization (NTP, chrony).</li>
                <li>Configuring chrony for time sync.</li>
                <li>Setting time zones (timedatectl).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure chrony and set the system time zone.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 27: Managing Containers with Podman",
          objective: "Learn the basics of containerization with Podman.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to containers and Podman.</li>
                <li>Running and managing containers.</li>
                <li>Understanding container images.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Run a simple web server container using Podman.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 28: Configuring a Web Server (Apache)",
          objective: "Learn to set up and manage an Apache web server.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Installing and configuring Apache (httpd).</li>
                <li>Managing virtual hosts.</li>
                <li>Securing Apache with SELinux and firewall.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure a virtual host for a website.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 29: Configuring a File Server (Samba)",
          objective: "Learn to set up a Samba file server.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Samba.</li>
                <li>Configuring Samba shares.</li>
                <li>Accessing Samba shares from a client.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a Samba share and access it from another system.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 30: Managing System Updates",
          objective: "Learn to manage system updates and patches.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Checking for system updates (dnf update).</li>
                <li>Automating updates with dnf-automatic.</li>
                <li>Applying kernel updates and rebooting safely.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Update the system and verify the kernel version.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 31: Backup and Recovery",
          objective: "Learn to perform system backups and recovery.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using tar for backups.</li>
                <li>Automating backups with scripts.</li>
                <li>Restoring from backups.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a backup of /etc and restore it.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 32: System Monitoring and Performance",
          objective: "Learn to monitor system performance.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Monitoring CPU, memory, and disk usage (top, vmstat).</li>
                <li>Analyzing I/O performance (iostat).</li>
                <li>Using sar for historical data.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Monitor system performance during a stress test.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 33: Troubleshooting Boot Issues",
          objective: "Learn to troubleshoot boot problems.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Accessing rescue mode.</li>
                <li>Resetting the root password.</li>
                <li>Fixing GRUB2 issues.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Reset the root password in rescue mode.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 34: Troubleshooting Network Issues",
          objective: "Learn to troubleshoot network problems.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Diagnosing connectivity issues (ping, traceroute).</li>
                <li>Checking network services (ss, netstat).</li>
                <li>Debugging firewall rules.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Troubleshoot a network connectivity issue.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 35: Troubleshooting Storage Issues",
          objective: "Learn to troubleshoot storage problems.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Diagnosing disk space issues (df, du).</li>
                <li>Fixing file system errors (fsck).</li>
                <li>Recovering LVM volumes.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Diagnose and fix a file system error.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 36: Configuring Kernel Parameters",
          objective: "Learn to tune the kernel using sysctl.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding kernel parameters.</li>
                <li>Using sysctl to modify parameters.</li>
                <li>Persisting changes in /etc/sysctl.conf.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Modify a kernel parameter (e.g., swappiness).</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 37: Managing Systemd Timers",
          objective: "Learn to schedule tasks using systemd timers.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to systemd timers.</li>
                <li>Creating a timer unit file.</li>
                <li>Comparing timers with cron.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a systemd timer to run a backup script.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 38: Managing Quotas",
          objective: "Learn to configure disk quotas for users.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding disk quotas.</li>
                <li>Enabling quotas on a file system.</li>
                <li>Setting user and group quotas (edquota).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure a disk quota for a user.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 39: Configuring Access Control Lists (ACLs)",
          objective: "Learn to manage permissions with ACLs.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to ACLs.</li>
                <li>Setting and viewing ACLs (setfacl, getfacl).</li>
                <li>Default ACLs for directories.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure ACLs for a shared directory.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 40: Preparing for the RHCSA Exam – System Administration",
          objective: "Review key system administration tasks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review user, group, and permission management.</li>
                <li>Practice service and process management.</li>
                <li>Mock scenarios for troubleshooting.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Perform a mock system administration task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 41: Preparing for the RHCSA Exam – Storage Management",
          objective: "Review storage management tasks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review partitioning, LVM, and file systems.</li>
                <li>Practice mounting and quota management.</li>
                <li>Mock storage troubleshooting scenarios.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Perform a mock storage configuration task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 42: Preparing for the RHCSA Exam – Network and Security",
          objective: "Review network and security tasks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review network configuration and troubleshooting.</li>
                <li>Practice firewall and SELinux configurations.</li>
                <li>Mock network and security scenarios.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Perform a mock network troubleshooting task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 43: Final Project – Planning",
          objective: "Plan the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of the final project.</li>
                <li>
                  Suggested project ideas (e.g., Configure a File Server).
                </li>
                <li>Planning features and structure.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a project plan and outline.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 44: Final Project – Development",
          objective: "Develop the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implementing core features.</li>
                <li>
                  Integrating learned concepts (e.g., storage, networking).
                </li>
                <li>Testing and debugging.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build the final project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 45: Final Project – Presentation",
          objective: "Present the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preparing a demo.</li>
                <li>Discussing challenges and solutions.</li>
                <li>Presenting to peers.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present the completed project.</li>
              </ul>
            </>
          ),
        },
      ],
    },
    learningOutcomes: {
      title: "Learning Outcomes",
      content: (
        <>
          <p className="text-sm">
            By the end of this course, learners will be able to:
          </p>
          <ul className="list-disc pl-4 text-xs">
            <li>
              Administer RHEL systems, including user and group management.
            </li>
            <li>Manage storage using partitions, LVM, and file systems.</li>
            <li>Configure and troubleshoot network settings.</li>
            <li>Secure systems using firewalld and SELinux.</li>
            <li>Prepare for the RHCSA certification exam.</li>
          </ul>
        </>
      ),
    },
    finalProjectIdeas: {
      title: "Suggested Final Project Ideas",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              <span className="font-bold text-sm">File Server Setup:</span>{" "}
              Configure a Samba or NFS server with user access controls and
              SELinux.
            </li>
            <li>
              <span className="font-bold text-sm">
                Web Hosting Environment:
              </span>{" "}
              Set up an Apache web server with virtual hosts, firewall rules,
              and backups.
            </li>
            <li>
              <span className="font-bold text-sm">
                System Monitoring Script:
              </span>{" "}
              Create a script to monitor system performance and send alerts.
            </li>
          </ul>
        </>
      ),
    },
    additionalNotes: {
      title: "Additional Notes",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              Practice with Red Hat’s official labs or online platforms like
              Linux Academy.
            </li>
            <li>
              Review the RHCSA exam objectives regularly (available on Red Hat’s
              website).
            </li>
            <li>Use discussion time for troubleshooting practice and Q&A.</li>
          </ul>
          <p className="text-sm">
            This syllabus covers essential RHCSA topics over 45 days, preparing
            learners for certification and real-world system administration.
          </p>
        </>
      ),
    },
  };

  // Function to handle enrollment (replace with actual enrollment logic)
  const handleEnroll = () => {
    alert("Enroll Now clicked! (Add your enrollment logic here)");
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      {/* Hero Section */}
      <header className="bg-gradient-to-r from-red-400 to-indigo-600 py-8 text-center">
        <h1 className="text-2xl font-bold mb-1 text-white">RHCSA Syllabus</h1>
        <p className="text-sm text-white">
          Master Red Hat Certified System Administrator (45 Hours – 1 Hour Per
          Day)
        </p>
      </header>

      {/* Training Availability Banner */}
      <div className="fixed top-34 left-80v right-4 bg-yellow-200 border border-yellow-400 rounded-lg p-3 text-center shadow-md z-10">
        <h2 className="text-sm font-semibold text-indigo-800">
          Training Available
        </h2>
        <p className="text-xs text-gray-700">Online Class | Physical Class</p>
        <button
          onClick={handleEnroll}
          className="mt-2 px-4 py-1 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition duration-300"
        >
          Enroll Now
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-2 pt-16 pb-16">
        {/* Course Overview */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("courseOverview")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.courseOverview.title}
            <span>{expandedSections["courseOverview"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["courseOverview"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.courseOverview.content}
            </div>
          )}
        </div>

        {/* Daily Schedule */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("dailySchedule")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.dailySchedule.title}
            <span>{expandedSections["dailySchedule"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["dailySchedule"] && (
            <div className="p-1 bg-white">
              {syllabusData.dailySchedule.days.map((day, index) => (
                <div
                  key={index}
                  className="mb-1 border border-gray-100 rounded-lg"
                >
                  <h3
                    onClick={() => toggleSection(`day${index}`)}
                    className="bg-gray-50 p-1 cursor-pointer flex justify-between items-center text-xs font-medium text-gray-600 hover:bg-gray-100"
                  >
                    {day.day}
                    <span>{expandedSections[`day${index}`] ? "▲" : "▼"}</span>
                  </h3>
                  {expandedSections[`day${index}`] && (
                    <div className="p-1 bg-white text-gray-600 leading-tight">
                      <p className="font-bold text-sm">Objective:</p>{" "}
                      {day.objective}
                      {day.topics}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Learning Outcomes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("learningOutcomes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.learningOutcomes.title}
            <span>{expandedSections["learningOutcomes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["learningOutcomes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.learningOutcomes.content}
            </div>
          )}
        </div>

        {/* Final Project Ideas */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("finalProjectIdeas")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.finalProjectIdeas.title}
            <span>{expandedSections["finalProjectIdeas"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["finalProjectIdeas"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.finalProjectIdeas.content}
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("additionalNotes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.additionalNotes.title}
            <span>{expandedSections["additionalNotes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["additionalNotes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.additionalNotes.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RHCSA;
